import React, { useEffect, useState, useCallback, useRef } from "react";
import { TrendingUp, TrendingDown, Target, AlertTriangle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

// === Sparkline component — TradingView-style mini chart ===
function Sparkline({ data, entry, tp, stop, width = 280, height = 80, trades = [] }) {
  if (!data || data.length < 2) {
    return <div style={{ width, height, display: "flex", alignItems: "center", justifyContent: "center", color: "#666", fontSize: 11 }}>no data yet</div>;
  }
  const prices = data.map(d => d.p);
  const min = Math.min(...prices, stop || prices[0], entry || prices[0]) * 0.998;
  const max = Math.max(...prices, tp || prices[0], entry || prices[0]) * 1.002;
  const range = max - min || 1;

  const xAt = (i) => (i / (data.length - 1)) * (width - 4) + 2;
  const yAt = (price) => height - 4 - ((price - min) / range) * (height - 8);

  // Build path
  const path = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xAt(i)} ${yAt(d.p)}`).join(" ");

  const cur = prices[prices.length - 1];
  const isUp = cur >= entry;
  const lineColor = isUp ? "#22c55e" : "#ef4444";

  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      {/* TP line (green dashed) */}
      {tp && yAt(tp) > 0 && yAt(tp) < height && (
        <>
          <line x1="0" y1={yAt(tp)} x2={width} y2={yAt(tp)} stroke="#22c55e" strokeWidth="1" strokeDasharray="3,3" opacity="0.6" />
          <text x={width - 4} y={yAt(tp) - 2} fill="#22c55e" fontSize="9" textAnchor="end">TP ${tp.toFixed(2)}</text>
        </>
      )}
      {/* Stop loss (red dashed) */}
      {stop && yAt(stop) > 0 && yAt(stop) < height && (
        <>
          <line x1="0" y1={yAt(stop)} x2={width} y2={yAt(stop)} stroke="#ef4444" strokeWidth="1" strokeDasharray="3,3" opacity="0.5" />
          <text x={width - 4} y={yAt(stop) + 9} fill="#ef4444" fontSize="9" textAnchor="end">SL ${stop.toFixed(2)}</text>
        </>
      )}
      {/* Entry line (gray solid) */}
      {entry && yAt(entry) > 0 && yAt(entry) < height && (
        <>
          <line x1="0" y1={yAt(entry)} x2={width} y2={yAt(entry)} stroke="#888" strokeWidth="1" opacity="0.7" />
          <text x="2" y={yAt(entry) - 2} fill="#aaa" fontSize="9">entry ${entry.toFixed(2)}</text>
        </>
      )}
      {/* Price line */}
      <path d={path} stroke={lineColor} strokeWidth="1.5" fill="none" />
      {/* Current price dot */}
      <circle cx={xAt(data.length - 1)} cy={yAt(cur)} r="3" fill={lineColor} />

      {/* Trade markers */}
      {trades.slice(0, 10).map((tr, idx) => {
        const tts = new Date(tr.timestamp).getTime();
        // Find closest data point
        let closestIdx = -1;
        let closestDiff = Infinity;
        data.forEach((d, i) => {
          const dts = new Date(d.t).getTime();
          const diff = Math.abs(dts - tts);
          if (diff < closestDiff) { closestDiff = diff; closestIdx = i; }
        });
        if (closestIdx === -1 || closestDiff > 2 * 3600 * 1000) return null;
        const isBuy = tr.type && tr.type.startsWith("BUY") || tr.type === "BASE_ORDER";
        const x = xAt(closestIdx);
        const y = yAt(tr.price);
        return (
          <g key={idx}>
            <circle cx={x} cy={y} r="3" fill={isBuy ? "#3b82f6" : "#f59e0b"} stroke="white" strokeWidth="0.5" />
            <text x={x} y={y - 5} fontSize="8" fill={isBuy ? "#3b82f6" : "#f59e0b"} textAnchor="middle">
              {isBuy ? "B" : "S"}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// === Per-Deal Card ===
function DealCard({ deal }) {
  const [showReason, setShowReason] = React.useState(false);
  const isWin = deal.pnl_pct >= 0;
  const isShort = deal.side === "SHORT";
  // For SHORT: progress = how much price dropped vs distance to TP
  const tpProgress = deal.entry && deal.tp_target && deal.entry !== deal.tp_target
    ? isShort
      ? ((deal.entry - deal.current) / (deal.entry - deal.tp_target)) * 100
      : ((deal.current - deal.entry) / (deal.tp_target - deal.entry)) * 100
    : 0;
  const tpPct = Math.max(0, Math.min(100, tpProgress));

  // Strategy color
  const stratColor = {
    DCA: "#3b82f6",
    MOMENTUM: "#f59e0b",
    MEAN_REVERSION: "#10b981",
    MAGNIFICENT_7: "#a855f7",
    SCALPER_LONG: "#06b6d4",
    SCALPER_SHORT: "#ec4899",
  }[deal.strategy] || "#6b7280";

  return (
    <div style={{
      background: "#1a1d29",
      border: `1px solid ${isWin ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.2)"}`,
      borderRadius: 12,
      padding: 14,
      transition: "all 0.2s",
    }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", display: "flex", alignItems: "center", gap: 6 }}>
            {deal.ticker}
            {isShort && <span style={{ fontSize: 10, padding: "1px 6px", background: "#ec4899", color: "#fff", borderRadius: 4 }}>SHORT</span>}
          </div>
          <div style={{ fontSize: 10, color: stratColor, fontWeight: 600, letterSpacing: 0.5 }}>
            {deal.strategy}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{
            fontSize: 16, fontWeight: 700,
            color: isWin ? "#22c55e" : "#ef4444",
            display: "flex", alignItems: "center", gap: 4,
          }}>
            {isWin ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {deal.pnl_pct > 0 ? "+" : ""}{deal.pnl_pct.toFixed(2)}%
          </div>
          <div style={{ fontSize: 11, color: isWin ? "#22c55e" : "#ef4444" }}>
            {deal.pnl > 0 ? "+" : ""}${deal.pnl.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Sparkline */}
      <div style={{ marginTop: 6, marginBottom: 8 }}>
        <Sparkline
          data={deal.sparkline}
          entry={deal.entry}
          tp={deal.tp_target}
          stop={deal.stop_loss}
          trades={deal.trades || []}
          width={300}
          height={70}
        />
      </div>

      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, fontSize: 11, marginTop: 6 }}>
        <div>
          <div style={{ color: "#888" }}>Entry</div>
          <div style={{ color: "#ddd", fontWeight: 500 }}>${deal.entry.toFixed(2)}</div>
        </div>
        <div>
          <div style={{ color: "#888" }}>Now</div>
          <div style={{ color: "#fff", fontWeight: 700 }}>${deal.current.toFixed(2)}</div>
        </div>
        <div>
          <div style={{ color: "#22c55e" }}>TP</div>
          <div style={{ color: "#22c55e", fontWeight: 500 }}>${deal.tp_target.toFixed(2)}</div>
        </div>
      </div>

      {/* TP progress bar */}
      <div style={{ marginTop: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: "#666", marginBottom: 2 }}>
          <span>Progress to TP (+{deal.tp_pct}%)</span>
          <span>{tpPct.toFixed(0)}%</span>
        </div>
        <div style={{
          height: 4, background: "#0a0c14", borderRadius: 2, overflow: "hidden",
        }}>
          <div style={{
            height: "100%",
            width: `${tpPct}%`,
            background: tpPct >= 100 ? "#22c55e" : tpPct >= 50 ? "linear-gradient(90deg,#f59e0b,#22c55e)" : "#3b82f6",
            transition: "width 0.5s",
          }} />
        </div>
      </div>

      {/* Footer line: shares, value, trades count */}
      <div style={{
        marginTop: 8, paddingTop: 6, borderTop: "1px solid #2a2d39",
        display: "flex", justifyContent: "space-between", fontSize: 10, color: "#888",
      }}>
        <span>{deal.shares.toFixed(4)} sh × ${deal.current.toFixed(2)} = <b style={{ color: "#ddd" }}>${deal.value.toFixed(2)}</b></span>
        <span>{deal.trades?.length || 0} trades</span>
      </div>
      {deal.safety_orders_filled !== undefined && (
        <div style={{ fontSize: 9, color: "#666", marginTop: 2 }}>
          SO: {deal.safety_orders_filled}/{deal.max_so} • avail ${deal.available?.toFixed(0)}
        </div>
      )}
      {deal.trim_tier > 0 && (
        <div style={{ fontSize: 9, color: "#22c55e", marginTop: 2 }}>
          Trimmed Tier {deal.trim_tier} {deal.peak_pnl_pct ? `(peak +${deal.peak_pnl_pct.toFixed(1)}%)` : ""}
        </div>
      )}

      {/* === REASONING (expandable) === */}
      {deal.reason && (
        <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px solid #2a2d39" }}>
          <button
            onClick={() => setShowReason(!showReason)}
            style={{
              width: "100%",
              background: showReason ? "#252836" : "transparent",
              border: "1px solid #2a2d39",
              borderRadius: 6,
              padding: "5px 8px",
              color: "#aaa",
              fontSize: 10,
              cursor: "pointer",
              textAlign: "left",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>🧠 Why this trade?</span>
            <span>{showReason ? "▾" : "▸"}</span>
          </button>
          {showReason && (
            <div style={{ marginTop: 6, padding: 8, background: "#0d1018", borderRadius: 6, fontSize: 10.5, lineHeight: 1.5 }}>
              <div style={{ color: "#fff", fontWeight: 600, marginBottom: 4 }}>{deal.reason.title}</div>
              <div style={{ color: "#aaa", marginBottom: 6 }}>
                <span style={{ color: "#888", fontWeight: 600 }}>WHY: </span>
                {deal.reason.why}
              </div>
              <div style={{ color: "#aaa", marginBottom: 6 }}>
                <span style={{ color: "#888", fontWeight: 600 }}>PLAN: </span>
                {deal.reason.plan}
              </div>
              <div style={{ color: isWin ? "#22c55e" : "#ef4444", fontWeight: 500 }}>
                <span style={{ color: "#888", fontWeight: 600 }}>STATUS: </span>
                {deal.reason.status}
              </div>
              {deal.signal && (
                <div style={{ marginTop: 6, padding: "3px 6px", background: "#1a1d29", borderRadius: 4, color: "#06b6d4", fontSize: 9.5 }}>
                  Signal: {deal.signal} • {deal.note}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DealsDashboard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const lastFetch = useRef(0);

  const fetchData = useCallback(async () => {
    if (!token) {
      setError("Not logged in. Please refresh and log in.");
      setLoading(false);
      return;
    }
    try {
      const r = await fetch(`${API}/api/skynet-brain/all-deals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 401) {
        setError("Session expired — please log out and log back in.");
        setLoading(false);
        return;
      }
      if (!r.ok) {
        setError(`HTTP ${r.status}`);
        setLoading(false);
        return;
      }
      const j = await r.json();
      if (j.error) {
        setError(j.error);
      } else if (j.detail) {
        setError(j.detail);
      } else {
        setData(j);
        setError(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      lastFetch.current = Date.now();
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (error && !data) {
    return (
      <div style={{ padding: 40, color: "#ef4444", textAlign: "center", maxWidth: 600, margin: "40px auto" }}>
        <div style={{ fontSize: 18, marginBottom: 8 }}>⚠️ {error}</div>
        <div style={{ fontSize: 13, color: "#888", marginTop: 12 }}>
          Tip: scroll up, click logout, then log in again to refresh your session.
        </div>
        <button
          onClick={fetchData}
          style={{
            marginTop: 16,
            padding: "8px 16px",
            background: "#3b82f6",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }
  if (loading && !data) {
    return <div style={{ padding: 40, color: "#888", textAlign: "center" }}>Loading deals… (wait ~5s)</div>;
  }
  if (!data) return null;

  const deals = data.deals || [];
  const filtered = filter === "all" ? deals
    : filter === "winners" ? deals.filter(d => d.pnl_pct > 0)
    : filter === "losers" ? deals.filter(d => d.pnl_pct < 0)
    : deals.filter(d => d.strategy === filter);

  const totals = data.totals || {};
  const totalNav = (totals.dca_free || 0) + (totals.gauntlet_cash || 0) + (totals.value || 0);
  const totalPnl = totalNav - (totals.total_cap || 10000);
  const totalPnlPct = (totalPnl / (totals.total_cap || 10000)) * 100;

  const winners = deals.filter(d => d.pnl_pct > 0).length;
  const losers = deals.filter(d => d.pnl_pct < 0).length;

  return (
    <div style={{ padding: "16px 20px", color: "#eee", maxWidth: 1600, margin: "0 auto" }}>
      {/* Hero stats */}
      <div style={{
        background: "linear-gradient(135deg, #0f1218, #1a1d29)",
        border: "1px solid #2a2d39",
        borderRadius: 16,
        padding: 20,
        marginBottom: 20,
      }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>TOTAL NAV</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>
              ${totalNav.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: totalPnl >= 0 ? "#22c55e" : "#ef4444" }}>
              {totalPnl >= 0 ? "+" : ""}${totalPnl.toFixed(2)} ({totalPnlPct >= 0 ? "+" : ""}{totalPnlPct.toFixed(2)}%)
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>CAPITAL</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#ddd" }}>${totals.total_cap || 0}</div>
            <div style={{ fontSize: 11, color: "#666" }}>
              DCA ${totals.dca_cap} + Gauntlet ${totals.gauntlet_cap}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>ACTIVE DEALS</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#3b82f6" }}>{data.n_deals}</div>
            <div style={{ fontSize: 11, color: "#666" }}>
              <span style={{ color: "#22c55e" }}>{winners}🟢</span> / <span style={{ color: "#ef4444" }}>{losers}🔴</span>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>FREE CASH</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: "#22c55e" }}>
              ${(totals.dca_free + totals.gauntlet_cash || 0).toFixed(0)}
            </div>
            <div style={{ fontSize: 11, color: "#666" }}>ready to deploy</div>
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "winners", "losers", "DCA", "MOMENTUM", "MEAN_REVERSION", "MAGNIFICENT_7", "SCALPER_LONG", "SCALPER_SHORT"].map(f => {
          const count = f === "all" ? deals.length
            : f === "winners" ? winners
            : f === "losers" ? losers
            : deals.filter(d => d.strategy === f).length;
          return (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                padding: "6px 12px",
                background: filter === f ? "#3b82f6" : "#1a1d29",
                color: filter === f ? "#fff" : "#aaa",
                border: "1px solid " + (filter === f ? "#3b82f6" : "#2a2d39"),
                borderRadius: 20,
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 500,
              }}
            >
              {f.replace("_", " ")} <span style={{ opacity: 0.7 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Deal grid */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
        gap: 12,
      }}>
        {filtered.map((d, i) => (
          <DealCard key={`${d.strategy}-${d.ticker}-${i}`} deal={d} />
        ))}
      </div>

      {filtered.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: "#666" }}>
          No deals match this filter
        </div>
      )}

      {/* Footer */}
      <div style={{ marginTop: 30, padding: 12, fontSize: 10, color: "#555", textAlign: "center" }}>
        Live • Auto-refresh every 15s • Last update: {data.as_of ? new Date(data.as_of).toLocaleTimeString() : "—"}
      </div>
    </div>
  );
}
